/**
 * Money in integer ngwee: the TypeScript mirror of engine/src/tisunge_engine/money.py.
 *
 * Amounts are whole ngwee (K1.00 = 100 ngwee). Exact maths uses BigInt wherever a
 * product can pass 2^53, and results round half-up to the ngwee. A total that must be
 * split uses the largest-remainder method so the parts add up exactly; ties go to the
 * lower index (rows are in member-number order).
 *
 * These functions must give the same answers as the Python engine, to the ngwee. The
 * shared vectors in engine/tests/vectors/ are run against both.
 */

export const NGWEE_PER_KWACHA = 100

export type MoneyErrorCode =
  | 'empty' // nothing was entered
  | 'invalid' // not an amount at all
  | 'too_many_decimals' // more than two decimal places
  | 'bad_grouping' // commas that are not thousands separators, e.g. "1,23"
  | 'too_large' // beyond what a JavaScript number holds exactly
  | 'not_whole_ngwee' // a number with a fraction of a ngwee
  | 'negative_weight'
  | 'zero_weights'

/** An amount that cannot be read, represented or split exactly. */
export class MoneyError extends Error {
  readonly code: MoneyErrorCode
  readonly input: string | undefined

  constructor(code: MoneyErrorCode, message: string, input?: string) {
    super(message)
    this.name = 'MoneyError'
    this.code = code
    this.input = input
  }
}

type Ngwee = number | bigint

function toBig(value: Ngwee, what = 'amount'): bigint {
  if (typeof value === 'bigint') return value
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError('not_whole_ngwee', `${what} must be a whole number of ngwee, got ${value}`)
  }
  return BigInt(value)
}

function abs(value: bigint): bigint {
  return value < 0n ? -value : value
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export interface FormatKwachaOptions {
  /** Money owed shows a minus sign and the word "owed": "-K1,234.00 owed". */
  owed?: boolean
  /** Two decimals (the default) on statements and receipts; whole kwacha in inputs. */
  decimals?: boolean
}

/**
 * Format ngwee the way the PRD prescribes: K1,234.00.
 *
 * Mirrors Python `format_kwacha`: negative amounts get a leading minus ("-K12.00"),
 * `owed` always gives "-K1,234.00 owed" whatever the sign, and `decimals: false`
 * drops the ngwee without rounding.
 */
export function formatKwacha(ngwee: Ngwee, options: FormatKwachaOptions = {}): string {
  const { owed = false, decimals = true } = options
  const value = toBig(ngwee)
  const magnitude = abs(value)
  const whole = magnitude / 100n
  const part = magnitude % 100n
  const text = `K${groupThousands(whole.toString())}` + (decimals ? `.${part.toString().padStart(2, '0')}` : '')
  if (owed) return `-${text} owed`
  return value < 0n ? `-${text}` : text
}

/**
 * Whole kwacha with thousands separators and no "K", for inputs: 123400 → "1,234".
 * Drops any ngwee (inputs take whole kwacha).
 */
export function formatKwachaPlain(ngwee: Ngwee, options: { decimals?: boolean } = {}): string {
  const text = formatKwacha(ngwee, { decimals: options.decimals ?? false })
  return text.replace('K', '')
}

/**
 * Read an amount typed by a person: "1,234.50", "K500", "k 1,000", "-K12", "0.5".
 *
 * Stricter than Python's `to_ngwee` on purpose. It refuses a third decimal place
 * even when it is zero, and commas that are not thousands separators, because
 * "1.230" or "1,23" typed by a person more likely mean K1,230 or K1.23 than what a
 * computer would read. Throws `MoneyError` with a code the UI turns into words.
 */
export function toNgwee(input: string): number {
  const raw = input
  let text = input.trim()
  if (text === '') throw new MoneyError('empty', 'enter an amount', raw)

  let negative = false
  if (text.startsWith('-') || text.startsWith('−')) {
    negative = true
    text = text.slice(1).trimStart()
  } else if (text.startsWith('+')) {
    text = text.slice(1).trimStart()
  }
  if (/^k/i.test(text)) text = text.slice(1).trimStart()
  if (!negative && (text.startsWith('-') || text.startsWith('−'))) {
    negative = true
    text = text.slice(1).trimStart()
  }

  const match = /^(\d[\d,]*)?(?:\.(\d*))?$/.exec(text)
  if (!match || text === '' || text === '.') throw new MoneyError('invalid', `not an amount: ${raw}`, raw)
  const integerPart = match[1] ?? ''
  const fraction = match[2] ?? ''
  if (integerPart === '' && fraction === '') throw new MoneyError('invalid', `not an amount: ${raw}`, raw)
  if (integerPart.includes(',') && !/^\d{1,3}(,\d{3})+$/.test(integerPart)) {
    throw new MoneyError('bad_grouping', `commas must separate thousands: ${raw}`, raw)
  }
  if (fraction.length > 2) {
    throw new MoneyError('too_many_decimals', `more than two decimal places: ${raw}`, raw)
  }

  const digits = (integerPart.replaceAll(',', '') || '0') + fraction.padEnd(2, '0')
  const ngwee = BigInt(digits)
  if (ngwee > BigInt(Number.MAX_SAFE_INTEGER)) throw new MoneyError('too_large', `too large: ${raw}`, raw)
  const result = Number(ngwee)
  return negative && result !== 0 ? -result : result
}

/** Whole kwacha to ngwee: 500 → 50000. */
export function kwachaToNgwee(kwacha: number): number {
  if (!Number.isSafeInteger(kwacha)) throw new MoneyError('invalid', `not whole kwacha: ${kwacha}`)
  const ngwee = kwacha * NGWEE_PER_KWACHA
  if (!Number.isSafeInteger(ngwee)) throw new MoneyError('too_large', `too large: ${kwacha}`)
  return ngwee
}

/**
 * Exact numerator/denominator rounded half-up (halves away from zero), like Python
 * `round_half_up(Fraction(numerator, denominator))`.
 */
export function roundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('invalid', 'division by zero')
  const negative = (numerator < 0n) !== (denominator < 0n) && numerator !== 0n
  const n = abs(numerator)
  const d = abs(denominator)
  let whole = n / d
  if ((n % d) * 2n >= d) whole += 1n
  return negative ? -whole : whole
}

/** Parse a decimal rate such as "0.05" or "5%" into an exact fraction. */
function parseRate(rate: string): { numerator: bigint; denominator: bigint } {
  const text = rate.trim()
  const percent = text.endsWith('%')
  const body = percent ? text.slice(0, -1).trim() : text
  const match = /^(-)?(\d*)(?:\.(\d+))?$/.exec(body)
  if (!match || (match[2] === '' && match[3] === undefined)) throw new MoneyError('invalid', `not a rate: ${rate}`, rate)
  const fraction = match[3] ?? ''
  let numerator = BigInt((match[2] || '0') + fraction)
  let denominator = 10n ** BigInt(fraction.length)
  if (percent) denominator *= 100n
  if (match[1]) numerator = -numerator
  return { numerator, denominator }
}

/**
 * `amount × rate`, rounded half-up to the ngwee, like Python `apply_rate`
 * (for example 5% monthly interest). The rate is a decimal string: "0.05" or "5%".
 */
export function applyRate(amountNgwee: Ngwee, rate: string): bigint {
  const { numerator, denominator } = parseRate(rate)
  return roundHalfUp(toBig(amountNgwee) * numerator, denominator)
}

/**
 * Split `total` ngwee in proportion to `weights` so the parts add up exactly to `total`.
 *
 * Mirrors Python `split_largest_remainder` with integer weights and index keys. Each
 * part is the exact share rounded down; the ngwee left over go one each to the
 * largest fractional remainders, ties to the lower index. Every exact share has the
 * same denominator (the weight sum), so comparing `total × w mod W` compares the
 * remainders exactly, with no floating point anywhere.
 */
export function splitLargestRemainder(total: bigint, weights: readonly bigint[]): bigint[] {
  if (weights.some((w) => w < 0n)) throw new MoneyError('negative_weight', 'weights must not be negative')
  const weightSum = weights.reduce((sum, w) => sum + w, 0n)
  if (weightSum === 0n) {
    if (total === 0n) return weights.map(() => 0n)
    throw new MoneyError('zero_weights', 'cannot split a non-zero total over zero weights')
  }

  const sign = total < 0n ? -1n : 1n
  const magnitude = abs(total)
  const parts: bigint[] = []
  const remainders: bigint[] = []
  for (const w of weights) {
    const product = magnitude * w
    parts.push(product / weightSum)
    remainders.push(product % weightSum)
  }
  const leftover = magnitude - parts.reduce((sum, p) => sum + p, 0n)

  const ranked = weights
    .map((_, index) => index)
    .sort((a, b) => {
      const ra = remainders[a] ?? 0n
      const rb = remainders[b] ?? 0n
      if (ra !== rb) return ra > rb ? -1 : 1
      return a - b
    })
  for (let i = 0; i < Number(leftover); i++) {
    const index = ranked[i]
    if (index !== undefined) parts[index] = (parts[index] ?? 0n) + 1n
  }
  return parts.map((p) => sign * p)
}

/**
 * Exact ratio as a percentage string rounded half-up, like Python `percent`:
 * percent(1, 3) === "33.3333". A zero denominator gives "0.0000".
 */
export function percent(numerator: Ngwee, denominator: Ngwee, places = 4): string {
  const num = toBig(numerator, 'numerator')
  const den = toBig(denominator, 'denominator')
  if (den === 0n) return places ? `0.${'0'.repeat(places)}` : '0'
  const unit = 10n ** BigInt(places)
  const scaled = roundHalfUp(num * 100n * unit, den)
  const sign = scaled < 0n ? '-' : ''
  const magnitude = abs(scaled)
  const whole = magnitude / unit
  const fraction = magnitude % unit
  return places ? `${sign}${whole}.${fraction.toString().padStart(places, '0')}` : `${sign}${whole}`
}

/**
 * How a screen reader should hear an amount: 123450 → "1,234 kwacha 50 ngwee".
 * Whole amounts drop the ngwee ("1,234 kwacha"); amounts under K1 read as ngwee
 * only ("50 ngwee"). Kwacha and ngwee are Zambian words and are never translated.
 */
export function kwachaForScreenReader(ngwee: Ngwee): string {
  const value = toBig(ngwee)
  const magnitude = abs(value)
  const whole = magnitude / 100n
  const part = magnitude % 100n
  let text: string
  if (whole === 0n && part > 0n) text = `${part} ngwee`
  else if (part === 0n) text = `${groupThousands(whole.toString())} kwacha`
  else text = `${groupThousands(whole.toString())} kwacha ${part} ngwee`
  return value < 0n ? `minus ${text}` : text
}

/** Sum ngwee amounts exactly; throws if the total no longer fits a JavaScript number. */
export function sumNgwee(values: readonly number[]): number {
  const total = values.reduce((sum, v) => sum + toBig(v), 0n)
  if (total > BigInt(Number.MAX_SAFE_INTEGER) || total < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new MoneyError('too_large', 'total is too large')
  }
  return Number(total)
}
